import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { CustomButton } from "../../ui/CustomButton";
import { BaseModal } from "../../ui/BaseModal";
import React, { useEffect, useState } from "react";
import { DescriptionBlock } from "./DescriptionBlock";
import { ImportedDataBlock } from "./ImportedDataBlock";
import { usePatchCampaignMutation } from "../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { ButtonTabs, ReuseModal, ReuseTooltip } from "../../ui";
import { NAME_REGEX, NO_SCRIPT_REGEX } from "../../../utils/regex";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { WarningIcon } from "../../images";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onSubmit: () => void;
  campaign_id: string;
  initProductUrl: string;
  initData: Fields;
};

const tabList = ["Description", "Imported Data"];
const initialTab = tabList[0];

export type Fields = {
  company_name: string;
  product_abstract: string;
  product_name: string;
  error?: { url?: string; files?: string[] };
};

export type Errors = { [key: string]: boolean };

const isDataChanged = (
  initData: Fields,
  formData: Fields,
  excludeKey?: string,
): boolean => {
  return Object.keys(initData)
    .filter((key) => key !== excludeKey)
    .some(
      (key) => initData[key as keyof Fields] !== formData[key as keyof Fields],
    );
};

const initialValues: Fields = {
  company_name: "",
  product_abstract: "",
  product_name: "",
};

const validation: { [key: string]: (value: string) => boolean } = {
  company_name: (value: string) => !NAME_REGEX.test(value),
  product_abstract: (value: string) => !value || !NO_SCRIPT_REGEX.test(value),
  product_name: (value: string) => !NAME_REGEX.test(value),
};

export const ProductDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  campaign_id,
  initData,
  initProductUrl,
}: Props) => {
  const [productUrl, setProductUrl] = useState<string>(initProductUrl);
  const [isDiscardModal, setIsDiscardModal] = useState<boolean>(false);
  const [isSubmitModal, setIsSubmitModal] = useState<boolean>(false);

  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState<Fields>(initialValues);

  const [errorMessage, setErrorMessage] = useState<Fields>(initialValues);

  const [error, setError] = useState<Errors>({
    company_name: false,
    product_abstract: false,
    product_name: false,
    url: false,
  });

  const [currentTab, setCurrentTab] = useState<string>(initialTab);

  const [
    patchCampaign,
    { isSuccess: patchCampaignSuccess, error: patchCampaignError, isLoading },
  ] = usePatchCampaignMutation();

  useEffect(() => {
    if (patchCampaignError) {
      if (getErrorMessage(patchCampaignError).fieldErrors) {
        setErrorMessage((err) => ({
          ...err,
          ...getErrorMessage(patchCampaignError).fieldErrors,
        }));
      } else {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(patchCampaignError).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Changes Were Not Saved"}
          />,
        );
      }
    }
  }, [patchCampaignError]);

  useEffect(() => {
    if (patchCampaignSuccess) {
      toast.success(
        <CustomToast
          type={"success"}
          text="This data will be the basis for further analysis of the companies"
          title={"Changes Were Saved"}
        />,
      );
      onSubmit();
    }
  }, [patchCampaignSuccess]);

  useEffect(() => {
    setFormData(initData);
  }, [initData]);

  const handleChange = (newValue: string) => {
    setCurrentTab(newValue);
  };

  const inputChangeHandler = (field: string, value: string) => {
    setErrorMessage((prev) => ({ ...prev, [field]: "" }));
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  const setErrorHandler = (field: string, value: boolean) => {
    setError((prev) => ({ ...prev, [field]: value }));
  };

  const setResultHandler = (result: Fields) => {
    setFormData(result);
    setProductUrl("");
    setPdfFiles([]);
    setCurrentTab(initialTab);
  };

  const submitHandler = () => {
    patchCampaign({
      campaign_id,
      ...formData,
    });
  };

  const checkIfBtnIsDisable = () => {
    const data = isDataChanged(initData, formData);
    return !data || Object.values(error).includes(true);
  };

  const submitIsNotAllowed = checkIfBtnIsDisable();

  const closeHandler = () => {
    if (
      submitIsNotAllowed &&
      productUrl === initProductUrl &&
      pdfFiles.length === 0
    ) {
      onClose();
    } else {
      setIsDiscardModal(true);
    }
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={closeHandler}
      title="Campaign Details"
      sx={{ padding: "32px", maxWidth: "540px", width: "94%" }}
      btnBlock={
        <Stack direction="row" gap="8px" paddingTop="8px">
          <CustomButton
            onClick={closeHandler}
            variant="contained"
            textColor="#4C545B"
            fullWidth
            type="button"
            customColor="#F1F1F1"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={() => setIsSubmitModal(true)}
            variant="contained"
            fullWidth
            type="button"
            customColor="#1C3C6C"
            disabled={submitIsNotAllowed || isLoading}
            startIcon={
              isLoading && (
                <CircularProgress size={20} sx={{ color: "#757575" }} />
              )
            }
          >
            Save Changes
          </CustomButton>
        </Stack>
      }
    >
      <Stack gap="16px">
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            Edit information about the campaign
          </Typography>
          <Box height="24px">
            <ReuseTooltip
              text={
                "Keep in mind that editing information about the campaign will delete old data and reset the analysis" +
                " of all accounts that were previously analyzed, except for manually selected buying committee members."
              }
            >
              <WarningIcon />
            </ReuseTooltip>
          </Box>
        </Stack>
        <ButtonTabs
          fullWidth
          currentTab={currentTab}
          onChange={handleChange}
          tabList={tabList}
        />
        {currentTab === initialTab ? (
          <DescriptionBlock
            formData={formData}
            inputChangeHandler={inputChangeHandler}
            error={error}
            setErrorHandler={setErrorHandler}
            errorMessage={errorMessage}
          />
        ) : (
          <ImportedDataBlock
            setResultHandler={setResultHandler}
            campaign_id={campaign_id}
            setPdfFiles={setPdfFiles}
            pdfFiles={pdfFiles}
            productUrl={productUrl}
            setProductUrl={setProductUrl}
            setErrorHandler={setErrorHandler}
            initProductUrl={initProductUrl}
          />
        )}
      </Stack>

      <ReuseModal
        isOpen={isDiscardModal}
        onClose={() => setIsDiscardModal(false)}
        onSubmit={() => {
          setIsDiscardModal(false);
          onClose();
        }}
        title="Discard Changes"
        text="Are you sure you want to discard all changes?"
        maxWidth="430px"
      />

      <ReuseModal
        icon="warn"
        isOpen={isSubmitModal}
        onClose={() => setIsSubmitModal(false)}
        onSubmit={() => {
          setIsSubmitModal(false);
          submitHandler();
        }}
        title="Restart the Analysis?"
        customText={
          <Stack gap="24px">
            <Typography variant="body1" color="text.secondary">
              New analysis will be run{" "}
              <span style={{ color: "#1F2932" }}>
                for already analyzed companies
              </span>{" "}
              to reflect description changes.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Previous data will no longer be available.
            </Typography>
          </Stack>
        }
        maxWidth="430px"
        submitBtnText="Continue"
      />
    </BaseModal>
  );
};
